import type { CSSProperties, ReactNode } from "react";
import {
  CELEBRATION_SHARDS,
  CELEBRATION_TWINKLES,
  type CelebrationShardShape,
} from "@/shared/components/ambient-celebration-backdrop.constants";
import { cn } from "@/shared/lib/cn";

interface AmbientCelebrationBackdropProps {
  tone?: "subtle" | "default";
}

export function AmbientCelebrationBackdrop({
  tone = "default",
}: AmbientCelebrationBackdropProps): ReactNode {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 -z-10 overflow-hidden",
        tone === "subtle" ? "opacity-35 dark:opacity-20" : "opacity-60 dark:opacity-40",
      )}
    >
      {CELEBRATION_SHARDS.map((shard) => (
        <span
          key={shard.id}
          className={`animate-confetti-drift absolute top-0 ${shard.color}`}
          style={
            {
              left: `${shard.left}%`,
              "--drift-duration": `${shard.duration}s`,
              "--drift-delay": `${shard.delay}s`,
              "--drift-x": `${shard.driftX}vw`,
              "--drift-rot": `${shard.rotation}deg`,
            } as CSSProperties
          }
        >
          <CelebrationShape variant={shard.shape} size={shard.size} />
        </span>
      ))}
      {CELEBRATION_TWINKLES.map((twinkle) => (
        <span
          key={twinkle.id}
          className="animate-twinkle absolute text-viz-1"
          style={
            {
              top: `${twinkle.top}%`,
              left: `${twinkle.left}%`,
              "--twinkle-duration": `${twinkle.duration}s`,
              "--twinkle-delay": `${twinkle.delay}s`,
            } as CSSProperties
          }
        >
          <CelebrationShape variant="star" size={twinkle.size} />
        </span>
      ))}
    </div>
  );
}

function CelebrationShape({
  variant,
  size,
}: {
  variant: CelebrationShardShape;
  size: number;
}): ReactNode {
  if (variant === "rect") {
    return (
      <span className="block rounded-sm bg-current" style={{ width: size, height: size * 0.45 }} />
    );
  }
  if (variant === "circle") {
    return <span className="block rounded-full bg-current" style={{ width: size, height: size }} />;
  }
  if (variant === "triangle") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 10 10"
        className="text-current"
        role="presentation"
      >
        <polygon points="5,0 10,10 0,10" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="text-current"
      role="presentation"
    >
      <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" fill="currentColor" />
    </svg>
  );
}
